import React, { useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Card,
    CardContent,
    Grid,
    Divider,
} from '@mui/material';
import {
    CheckCircle,
    Error,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Security as SecurityIcon
} from '@mui/icons-material';

import { usePostApiUsers } from '@/api/gen/api';

interface UserData {
    userName: string;
    email: string;
    fullName: string;
    phoneNumber: string;
    password: string;
    role: string; 
}

interface StepUserRegisterClientProps {
    onNext: (data: UserData, apiResponse: any) => void;
    onSkip: () => void;
    clientData: {
        id: number;
        clientName: string;
    };
    plantData: {
        id: number;
        plantName: string;
    };
}

// -------------------------- COMPONENTE PRINCIPAL --------------------------
const StepUserRegisterClient: React.FC<StepUserRegisterClientProps> = ({
    onNext,
    onSkip,
    clientData,
    plantData
}) => {
    // -------------------------- ESTADOS --------------------------
    const [formData, setFormData] = useState<UserData>({
        userName: '',
        email: '',
        fullName: '',
        phoneNumber: '',
        password: '',
        role: '' 
    });

    const [errors, setErrors] = useState<Partial<Record<keyof UserData, string>>>({});
    const [isSkipped, setIsSkipped] = useState(false);
    const [apiError, setApiError] = useState<string | null>(null);

    // -------------------------- HOOKS --------------------------
    const {
        mutate: createUser,
        isLoading,
        isSuccess,
        data: apiResponse,
        error: mutationError
    } = usePostApiUsers();


    // -------------------------- MANEJADORES --------------------------
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setApiError(null);
        
        if (validateForm() && !isLoading) {
            // envia sin roles 
            const apiData = {
                userName: formData.userName,
                email: formData.email,
                fullName: formData.fullName,
                phoneNumber: formData.phoneNumber || null,
                password: formData.password,
                roles: [] 
            };


            createUser({ data: apiData }, {
                onError: (error: any) => {
                
                    
                    if (error.response?.data?.errors) {
                        const errorMessages = error.response.data.errors.map((err: any) => err.description).join(', ');
                        setApiError(errorMessages);
                    } else if (error.response?.data?.message) {
                        setApiError(error.response.data.message);
                    } else {
                        setApiError('Error al crear el usuario. Verifique los datos.');
                    }
                },
                onSuccess: (data) => {
                    console.log('✅ Respuesta exitosa:', data);
                    if (data.success === false && data.errors) {
                        const errorMessages = data.errors.map((err: any) => err.description).join(', ');
                        setApiError(errorMessages);
                    }
                }
            });
        }
    };

    const handleSkip = () => {
        setIsSkipped(true);
        onSkip();
    };

    const handleContinueToNextStep = () => {
        if (apiResponse?.success) {
            onNext(formData, apiResponse);
        }
    };

    const handleInputChange = (field: keyof UserData) => (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Limpiar error del campo
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
        if (apiError) {
            setApiError(null);
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof UserData, string>> = {};

        if (!formData.userName.trim()) {
            newErrors.userName = 'El nombre de usuario es requerido';
        } else if (formData.userName.length < 3) {
            newErrors.userName = 'El nombre de usuario debe tener al menos 3 caracteres';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'El email es requerido';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'El formato del email no es válido';
        }

        if (!formData.fullName.trim()) {
            newErrors.fullName = 'El nombre completo es requerido';
        }

        if (!formData.password.trim()) {
            newErrors.password = 'La contraseña es requerida';
        } else if (formData.password.length < 8) {
            newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // -------------------------- RENDERIZADO BASADO EN RESPUESTA REAL --------------------------

    // Caso 1: Éxito real (success: true)
    if (isSuccess && apiResponse?.success === true) {
        return (
            <Box sx={{ mt: 2 }}>
                <Alert severity="success" icon={<CheckCircle />} sx={{ mb: 3 }}>
                Usuario creado exitosamente
                </Alert>

                <Typography variant="h5" gutterBottom color="primary">
                    Paso 4 Completado: Usuario Creado
                </Typography>

                <Card sx={{ mb: 3, border: '1px solid', borderColor: 'success.main' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom color="success.main">
                            Usuario Creado - ID: {apiResponse.id}
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Nombre de Usuario:</Typography>
                                <Typography variant="body1">{formData.userName}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Email:</Typography>
                                <Typography variant="body1">{formData.email}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Nombre Completo:</Typography>
                                <Typography variant="body1">{formData.fullName}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Rol Seleccionado:</Typography>
                                <Typography variant="body1">
                                    {formData.role || 'Ninguno (se creó sin roles debido a error del backend)'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="body2" color="text.secondary">Estado:</Typography>
                                <Typography variant="body1" color="success.main">Creado Exitosamente</Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleContinueToNextStep}
                        startIcon={<CheckCircle />}
                    >
                        Continuar al Siguiente Paso
                    </Button>
                </Box>
            </Box>
        );
    }

    // Caso 2: Éxito técnico pero fallo de negocio (success: false)
    if (isSuccess && apiResponse?.success === false) {
        return (
            <Box sx={{ mt: 2 }}>
                <Alert severity="error" icon={<Error />} sx={{ mb: 3 }}>
                    ❌ Error al crear el usuario
                </Alert>

                <Typography variant="h5" gutterBottom color="error">
                    Paso 4 Fallido
                </Typography>

                <Card sx={{ mb: 3, border: '1px solid', borderColor: 'error.main' }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom color="error.main">
                            Error en la creación del usuario
                        </Typography>

                        {apiResponse.errors && apiResponse.errors.map((apiError: any, index: number) => (
                            <Alert
                                key={index}
                                severity="warning"
                                sx={{ mb: 1 }}
                            >
                                <Typography variant="subtitle2">
                                    {apiError.description}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    Ubicación: {apiError.location}
                                </Typography>
                            </Alert>
                        ))}

                        <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                            <strong>Datos enviados:</strong>
                        </Typography>
                        <Box sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                            <pre style={{ fontSize: '0.75rem', margin: 0 }}>
                                {JSON.stringify(formData, null, 2)}
                            </pre>
                        </Box>
                    </CardContent>
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={() => window.location.reload()}
                    >
                        Volver a intentar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSkip}
                    >
                        Saltar este paso
                    </Button>
                </Box>
            </Box>
        );
    }

    // Caso 3: Usuario decidió saltar el paso
    if (isSkipped) {
        return (
            <Box sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ mb: 3 }}>
                    ⚠️ Paso de creación de usuario omitido
                </Alert>

                <Typography variant="h5" gutterBottom color="primary">
                    Paso 4 Omitido: Usuario Administrador
                </Typography>

                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="body1" sx={{ mb: 2 }}>
                            Has decidido omitir la creación del usuario administrador en este momento.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Puedes crear usuarios administradores más tarde desde el panel de administración.
                        </Typography>
                    </CardContent>
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleContinueToNextStep}
                        startIcon={<CheckCircle />}
                    >
                        Continuar al Siguiente Paso
                    </Button>
                </Box>
            </Box>
        );
    }

    // -------------------------- RENDERIZADO DEL FORMULARIO --------------------------

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mt: 1, mb: 3 }}>
                Este paso es opcional. Puedes crear un usuario administrador ahora o saltar este paso y crearlo más tarde.
                <br />
            </Alert>

            {/* Título */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <PersonIcon color='primary' sx={{ fontSize: 32 }} />
                <Typography variant='h4' component='h1' fontWeight='bold'>
                    Crear Usuario Administrador
                </Typography>
            </Box>

            {/* Información de Resumen */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <Card sx={{ 
                        height: 'fit-content', 
                        boxShadow: 3, 
                        borderRadius: 2,
                        backgroundColor: 'primary.50'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <PersonIcon color='primary' />
                                <Typography variant='h6' fontWeight='bold' color='primary.main'>
                                    Cliente
                                </Typography>
                            </Box>
                            <Typography variant="body1">
                                <strong>Nombre:</strong> {clientData.clientName}
                            </Typography>
                            <Typography variant="body1">
                                <strong>ID:</strong> {clientData.id}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card sx={{ 
                        height: 'fit-content', 
                        boxShadow: 3, 
                        borderRadius: 2,
                        backgroundColor: 'info.50'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                <SecurityIcon color='info' />
                                <Typography variant='h6' fontWeight='bold' color='info.main'>
                                    Planta
                                </Typography>
                            </Box>
                            <Typography variant="body1">
                                <strong>Nombre:</strong> {plantData.plantName}
                            </Typography>
                            <Typography variant="body1">
                                <strong>ID:</strong> {plantData.id}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Mostrar error de API */}
            {apiError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {apiError}
                </Alert>
            )}

            {/* Mostrar error de mutation */}
            {mutationError && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    Error de conexión: {(mutationError as any)?.message}
                </Alert>
            )}

            {/* Información Personal */}
            <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Información Personal
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Nombre de Usuario *"
                                value={formData.userName}
                                onChange={handleInputChange('userName')}
                                error={!!errors.userName}
                                helperText={errors.userName || 'Mínimo 3 caracteres'}
                                disabled={isLoading}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Correo Electrónico *"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange('email')}
                                error={!!errors.email}
                                helperText={errors.email || 'Email principal del usuario'}
                                disabled={isLoading}
                                required
                                InputProps={{
                                    startAdornment: (
                                        <EmailIcon sx={{ color: 'action.active', mr: 1, ml: 0.5 }} />
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Nombre Completo *"
                                value={formData.fullName}
                                onChange={handleInputChange('fullName')}
                                error={!!errors.fullName}
                                helperText={errors.fullName || 'Nombre y apellidos completos'}
                                disabled={isLoading}
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Número de Teléfono"
                                value={formData.phoneNumber}
                                onChange={handleInputChange('phoneNumber')}
                                disabled={isLoading}
                                helperText="Opcional"
                                InputProps={{
                                    startAdornment: (
                                        <PhoneIcon sx={{ color: 'action.active', mr: 1, ml: 0.5 }} />
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Credenciales de Acceso */}
            <Card sx={{ mb: 3, boxShadow: 3, borderRadius: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Credenciales de Acceso
                    </Typography>
                    <Divider sx={{ mb: 3 }} />
                    
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Contraseña *"
                                type="password"
                                value={formData.password}
                                onChange={handleInputChange('password')}
                                error={!!errors.password}
                                helperText={errors.password || 'Mínimo 8 caracteres'}
                                disabled={isLoading}
                                required
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Divider sx={{ my: 3 }} />

            {/* Botones de acción */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    * Campos obligatorios
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={handleSkip}
                        disabled={isLoading}
                    >
                        Saltar este paso
                    </Button>
                    
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={20} /> : null}
                    >
                        {isLoading ? 'Creando Usuario...' : 'Crear Usuario (Sin Roles)'}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default StepUserRegisterClient;